import React, { useState } from "react";
import PolicyLayout from "@/components/PolicyLayout";
import { ChevronDown, ChevronUp } from "lucide-react";

const GROUPS = [
  {
    group: "Ordering & Payments",
    items: [
      {
        q: "Which payment methods are supported?",
        a: "We accept UPI, credit/debit cards (Visa, Mastercard, RuPay, American Express), net-banking, popular prepaid wallets, and Cash on Delivery in serviceable pincodes. All online payments are processed by our RBI-authorised Payment Aggregator over PCI-DSS-certified infrastructure.",
      },
      {
        q: "Is Cash on Delivery (COD) available?",
        a: "COD is available on eligible orders and pincodes. Certain high-value orders or repeat COD refusals may make an account ineligible for COD, in line with Consumer Protection (E-Commerce) Rules, 2020 and our fraud-prevention framework.",
      },
      {
        q: "Are my card details stored?",
        a: "No. Florvanta does not store any card number, CVV, UPI PIN, or bank password. Where you opt to save a card, only an RBI-compliant Card-on-File token issued by the card network is retained by our Payment Aggregator.",
      },
      {
        q: "Is my data stored in India?",
        a: "Yes. Payment system data is stored on servers located in India in accordance with the RBI Circular on Storage of Payment System Data (6 April 2018).",
      },
      {
        q: "The amount was debited but my order is not confirmed. What now?",
        a: "Please wait as per the RBI TAT — UPI T+1 working day, cards T+5, net-banking T+3, wallets T+1. If credit is not received within TAT, contact support@florvanta.co.in with the UTR/RRN and your mobile number. Compensation of ₹100/day of delay (RBI TAT Circular) is credited automatically by your issuing bank if applicable.",
      },
    ],
  },
  {
    group: "Orders, Shipping & Delivery",
    items: [
      {
        q: "Do you ship across India?",
        a: "Yes. Clothing and Electronics are shipped PAN-India. Grocery is restricted to serviceable pincodes with a fulfilling local vendor.",
      },
      {
        q: "How long does delivery take?",
        a: "Typically 3-5 business days in metros and 5-9 business days in Tier-2/3 cities for Clothing and Electronics. Grocery is same-day or next-day in serviceable areas. Exact estimates are shown on the product page and at checkout.",
      },
      {
        q: "How do I track my order?",
        a: "Sign in and go to My Orders. Each parcel has its own tracking. Multi-vendor orders may ship in multiple parcels and can have different tracking IDs.",
      },
      {
        q: "The order arrived tampered. What should I do?",
        a: "Refuse delivery and immediately share a clear photograph with support@florvanta.co.in quoting your Order ID. We will fast-track a resolution.",
      },
    ],
  },
  {
    group: "Returns & Refunds",
    items: [
      {
        q: "What is your return policy?",
        a: "Clothing: 7 days. Electronics: 7 days (replacement first). Grocery packaged: 3 days. Grocery perishable: within 24 hours of delivery. Full details are in the Return Policy.",
      },
      {
        q: "How long do refunds take?",
        a: "Refunds are processed to the original source. For prepaid orders, refunds reach your bank within 5-7 working days after return QC or cancellation. Failed-transaction reversals follow the RBI TAT framework.",
      },
      {
        q: "What if my refund is delayed beyond RBI TAT?",
        a: "A compensation of ₹100 per calendar day of delay is payable directly by your issuing bank as per the RBI Harmonisation of TAT Circular (20 September 2019). Florvanta will co-operate with your bank on any dispute.",
      },
    ],
  },
  {
    group: "Selling on Florvanta",
    items: [
      {
        q: "How do I become a seller?",
        a: "Click 'Sell' at the top of the site and submit your business details (name, PAN, GSTIN where applicable, address, bank account). Once your KYC is verified, our team approves your store — usually within 24-48 hours.",
      },
      {
        q: "What is the commission?",
        a: "A flat 1% of the net sale value (excluding GST and shipping). We do not charge listing fees, monthly fees, or category subscription fees.",
      },
      {
        q: "When do sellers get paid?",
        a: "Settlements are initiated on a T+7 working day basis from the date of successful delivery and expiry of the return window, in line with the RBI Payment Aggregator settlement framework. Funds are credited via NEFT/IMPS to your registered bank account.",
      },
      {
        q: "Which documents are required for KYC?",
        a: "PAN card, GSTIN (if turnover exceeds statutory threshold or you are a registered business), business registration proof (Certificate of Incorporation, GST certificate, Shop Establishment license, etc.), cancelled cheque or bank statement, and address proof of the pickup location.",
      },
      {
        q: "Which products am I not allowed to sell?",
        a: "Counterfeits, replicas, drugs, tobacco, alcohol, weapons, wildlife products, currency, obscene content, and any items restricted under Indian law or the IT Rules 2021 are prohibited.",
      },
    ],
  },
  {
    group: "Account & Privacy",
    items: [
      {
        q: "How is my data protected?",
        a: "Passwords are stored as bcrypt salted hashes; sensitive data is encrypted at rest and in transit (TLS 1.2+). We follow the IT Act's Reasonable Security Practices (Rule 8, SPDI Rules 2011) and comply with the DPDPA 2023 where notified.",
      },
      {
        q: "How do I delete my account?",
        a: "Write to privacy@florvanta.co.in from your registered email. We will delete your account within 15 days, subject to statutory retention obligations (tax invoices retained 8 years under Section 36 CGST Act).",
      },
      {
        q: "Do you share my address with sellers?",
        a: "Only the fulfilling Vendor and the logistics partner receive your shipping name, address, and phone — solely for delivering your order.",
      },
    ],
  },
  {
    group: "Grievances",
    items: [
      {
        q: "How do I raise a grievance?",
        a: "Email grievance@florvanta.co.in with your Order ID and issue. As per Rule 3(2) of the IT Rules 2021 and Rule 5(3) of the Consumer Protection (E-Commerce) Rules 2020, we acknowledge within 48 hours and resolve within 15 days.",
      },
      {
        q: "What if the issue is not resolved in 15 days?",
        a: "You may escalate to the RBI Complaint Management System (CMS) for payment-related issues, or to the National Consumer Helpline (1915) / Consumer Commission for consumer-protection matters.",
      },
    ],
  },
];

function FAQItem({ q, a, id }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-[#E2E8E5] bg-white" data-testid={`faq-${id}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between text-left p-4"
      >
        <span className="text-[#0F3D2E] font-medium">{q}</span>
        {open ? <ChevronUp size={18} className="text-[#14A085]" /> : <ChevronDown size={18} className="text-[#14A085]" />}
      </button>
      {open && <div className="px-4 pb-4 text-[15px] leading-7 text-[#3B5249]">{a}</div>}
    </div>
  );
}

export default function FAQs() {
  return (
    <PolicyLayout
      title="Frequently Asked Questions"
      tagline="Everything you need to know about ordering, selling, payments, returns, and privacy on Florvanta."
      updated="February 2026"
      sections={GROUPS.map((g, i) => ({ id: `g-${i}`, label: g.group }))}
    >
      {GROUPS.map((g, i) => (
        <section key={i} id={`g-${i}`} className="mb-10 scroll-mt-24">
          <h2 className="font-display text-2xl text-[#0F3D2E] mb-3 tracking-tight">{g.group}</h2>
          <div className="space-y-3">
            {g.items.map((it, j) => (
              <FAQItem key={j} id={`${i}-${j}`} q={it.q} a={it.a} />
            ))}
          </div>
        </section>
      ))}
    </PolicyLayout>
  );
}
